import React from 'react';
import { Box, Typography, Button, Chip } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { PlanStep, StepKind, StepIntent } from '../../../../types/generated/graphql';
import { colors } from '../../../../theme/colors';

interface ActionPromptProps {
  step: PlanStep;
  onMarkComplete: () => void;
  onSendMessage: (message: string) => void;
  isMarkingComplete?: boolean;
}

/**
 * Generates contextual action text based on step title/description.
 */
function getActionContext(step: PlanStep): {
  actionVerb: string;
  completionPhrase: string;
  inputPlaceholder: string;
  quickResponses: string[];
} {
  const title = step.title.toLowerCase();
  const description = (step.description || '').toLowerCase();
  const combined = `${title} ${description}`;

  // Shopping/buying patterns
  if (combined.includes('shop') || combined.includes('buy') || combined.includes('purchase') || combined.includes('get from store')) {
    return {
      actionVerb: 'shop',
      completionPhrase: 'I finished shopping',
      inputPlaceholder: 'Any notes about what you bought or couldn\'t find?',
      quickResponses: ['Got everything!', 'Couldn\'t find some items', 'Substituted some ingredients'],
    };
  }

  // Preparation patterns
  if (combined.includes('prepare') || combined.includes('prep') || combined.includes('set up') || combined.includes('stage')) {
    return {
      actionVerb: 'prepare',
      completionPhrase: 'I\'m ready',
      inputPlaceholder: 'Any notes about the preparation?',
      quickResponses: ['All set up!', 'Ready to go', 'Had to adjust something'],
    };
  }

  // Checking/verifying patterns
  if (combined.includes('check') || combined.includes('verify') || combined.includes('confirm') || combined.includes('ensure')) {
    return {
      actionVerb: 'check',
      completionPhrase: 'I verified this',
      inputPlaceholder: 'What did you find?',
      quickResponses: ['All good!', 'Found an issue', 'Confirmed'],
    };
  }

  // Gathering/collecting patterns
  if (combined.includes('gather') || combined.includes('collect') || combined.includes('assemble')) {
    return {
      actionVerb: 'gather',
      completionPhrase: 'I gathered everything',
      inputPlaceholder: 'Any notes about what you collected?',
      quickResponses: ['Got everything!', 'Missing some items', 'All assembled'],
    };
  }

  // Decision patterns
  if (combined.includes('decide') || combined.includes('choose') || combined.includes('select') || combined.includes('pick')) {
    return {
      actionVerb: 'decide',
      completionPhrase: 'I made my decision',
      inputPlaceholder: 'What did you decide?',
      quickResponses: [],
    };
  }

  // Contact/communication patterns
  if (combined.includes('contact') || combined.includes('call') || combined.includes('email') || combined.includes('reach out')) {
    return {
      actionVerb: 'contact',
      completionPhrase: 'I reached out',
      inputPlaceholder: 'How did it go?',
      quickResponses: ['Left a message', 'Spoke with them', 'Sent an email'],
    };
  }

  // Schedule/book patterns
  if (combined.includes('schedule') || combined.includes('book') || combined.includes('reserve') || combined.includes('appointment')) {
    return {
      actionVerb: 'schedule',
      completionPhrase: 'I scheduled it',
      inputPlaceholder: 'When is it scheduled for?',
      quickResponses: ['Booked!', 'Waiting for confirmation'],
    };
  }

  // Review/read patterns
  if (combined.includes('review') || combined.includes('read') || combined.includes('look over') || combined.includes('go through')) {
    return {
      actionVerb: 'review',
      completionPhrase: 'I reviewed it',
      inputPlaceholder: 'Any notes or questions?',
      quickResponses: ['Looks good!', 'Have some questions', 'Found something to note'],
    };
  }

  // Default for generic action steps
  return {
    actionVerb: 'complete',
    completionPhrase: 'I did this',
    inputPlaceholder: 'Any notes about this step?',
    quickResponses: ['Done!', 'Completed', 'Need help'],
  };
}

/**
 * Determines if this step should show the action prompt.
 * Only for human action steps that don't require structured input.
 */
export function shouldShowActionPrompt(step: PlanStep): boolean {
  // Only for HUMAN steps
  if (step.kind !== StepKind.Human) return false;

  // Not for data collection/entry intents
  if (step.intent === StepIntent.DataEntry || step.intent === StepIntent.CollectDocument) {
    return false;
  }

  // Not if there's already an intake
  if (step.intake) return false;

  return true;
}

/**
 * Action prompt for simple human action steps.
 * Shows a clear "I did this" button and contextual quick responses.
 */
export const ActionPrompt: React.FC<ActionPromptProps> = ({
  step,
  onMarkComplete,
  onSendMessage,
  isMarkingComplete = false,
}) => {
  const context = getActionContext(step);

  const handleQuickResponse = (response: string) => {
    onSendMessage(response);
  };

  return (
    <Box
      sx={{
        px: 2,
        py: 2,
        borderTop: `1px solid ${colors.border}`,
        bgcolor: colors.gray[50],
      }}
    >
      {/* Main action prompt */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: context.quickResponses.length > 0 ? 1.5 : 0,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: colors.gray[600],
            fontWeight: 500,
          }}
        >
          When you're done:
        </Typography>

        <Button
          variant="contained"
          size="small"
          onClick={onMarkComplete}
          disabled={isMarkingComplete}
          startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 18 }} />}
          sx={{
            bgcolor: colors.green[600],
            '&:hover': { bgcolor: colors.green[700] },
            textTransform: 'none',
            fontWeight: 600,
            px: 2,
          }}
        >
          {context.completionPhrase}
        </Button>
      </Box>

      {/* Quick responses */}
      {context.quickResponses.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <ChatBubbleOutlineIcon sx={{ fontSize: 14, color: colors.gray[400], mr: 0.5 }} />
          <Typography variant="caption" sx={{ color: colors.gray[500], mr: 0.5 }}>
            Or tell us:
          </Typography>
          {context.quickResponses.map((response) => (
            <Chip
              key={response}
              label={response}
              size="small"
              onClick={() => handleQuickResponse(response)}
              sx={{
                cursor: 'pointer',
                bgcolor: colors.gray[100],
                color: colors.gray[700],
                fontWeight: 500,
                fontSize: '0.75rem',
                '&:hover': {
                  bgcolor: colors.blue[50],
                  color: colors.blue[700],
                },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ActionPrompt;

